// Who can reach the public site while it is not in Live mode.
//
// WHAT THIS GOVERNS
//
// Root's mode closes the site. While it is Demo or Test, anyone without
// an email address at the site's own domain sees a maintenance notice,
// and the public API answers 503 so a partner's widget shows maintenance
// rather than serving data that is not real.
//
// WHY ROOT IS NEVER EXEMPT
//
// The checkbox here lets ADMINS hold an address anywhere. It deliberately
// does not cover root, because root is the account that can change this
// setting — and a rule that can lock out the only person able to undo it
// is a trap. The backend refuses to save a domain root's own address
// would fail, and says which addresses the domain would accept.
import React, { useEffect, useState } from "react";
import { showToast } from "./toast.js";

function Frozen({ frozen, title, children }) {
  if (!frozen) return children;
  return (
    <fieldset disabled title={title} style={{ border: "none", padding: 0, margin: 0 }}>
      <div style={{ opacity: 0.55 }}>{children}</div>
    </fieldset>
  );
}

export default function SiteAccessPanel({ token, wsRequest, canEdit, modeStatus }) {
  const [settings, setSettings] = useState(null);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // DEMO ONLY, not "anything but Live".
  //
  // These are SETTINGS, and freezing them in Test mode meant switching to
  // Live to save a form and switching back to carry on testing — friction
  // with little behind it. Provider credentials already have separate
  // test and live slots, and the data on screen is gated by mode anyway.
  //
  // Demo stays frozen: root pulls it as a brake to show somebody the
  // product, and settings changing under a demonstration is a real
  // surprise. The server enforces the same rule in
  // assertSettingsEditable.
  const frozen = (modeStatus?.effectiveMode || "live") === "demo";
  const frozenTitle = "Leave Demo mode to manage this";
  const locked = !canEdit || frozen;

  const load = () => {
    // Its OWN action, on manage_settings — the permission this panel is
    // gated by. It previously called admin.siteSettings.private, which
    // requires manage_payment_providers and would have left an admin
    // without that permission staring at "Loading…" forever.
    wsRequest("admin.siteAccess.get", {}, token)
      .then((d) => {
        setSettings(d);
        setDomain(d.siteEmailDomain || "");
      })
      .catch((err) => {
        setLoadFailed(true);
        showToast(err.message || "Could not reach backend.", "error");
      });
  };

  // RELOADS WHEN THE MODE CHANGES, not just on mount. This panel shows
  // whether the lockdown is in effect, and which mode is in effect — switching
  // mode changed the answer and left the old one on screen.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [modeStatus?.effectiveMode]);

  const save = async (patch) => {
    setBusy(true);
    try {
      await wsRequest("admin.siteSettings.update", patch, token);
      showToast("Saved.");
      load();
    } catch (err) {
      // The backend's refusal explains itself — which addresses the
      // domain would accept, and that root would be locked out. Showing
      // it verbatim is more use than any wording invented here.
      showToast(err.message || "Could not reach backend.", "error");
    } finally {
      setBusy(false);
    }
  };

  // loadFailed, not the old `error` state: the message itself now goes to
  // a toast, but this still has to stop showing "Loading…" for ever when
  // the fetch fails.
  if (!settings && !loadFailed) return <p>Loading…</p>;

  const rule = settings?.siteDomainRule || {};
  const active = settings?.notLiveLockdownActive;
  // ---- ONE REASON AGAIN, SO READING IT AS "LIVE" IS SAFE AGAIN (1.904.0) ----
  //
  // SUPPRESSED IS NOT THE SAME AS LIVE, and this panel said it was.
  // notLiveLockdownActive was false for exactly one reason until 1.274.0 —
  // the site being Live — so reading it as "the site is Live" was safe.
  // TREAT_INSTANCE_AS_LIVE gave it a second reason, and this panel went on
  // announcing "The site is Live and open to everyone" to an operator looking
  // at a TEST-mode box. Worse than saying nothing: it stated the opposite of
  // the truth on the one screen dedicated to this rule.
  //
  // 1.903.0 removed the flag, so the predicate is one expression on root's
  // mode and an inactive lockdown means Live and nothing else. The branch that
  // read lockdownOverriddenByEnv went with it - the key is no longer in
  // admin.siteAccess.get, so it could not have fired - and this note stays
  // because the FAULT can come back: anything that makes the lockdown inactive
  // for a second reason has to be reported here before it is added.

  return (
    <section style={{ maxWidth: 640, marginTop: 28 }}>
      {/* The heading and the status line sit OUTSIDE the frozen wrapper.
          Frozen dims to 55% and disables its fieldset — right for
          controls, wrong for the one line telling you the lockdown is
          active, which matters most in exactly the modes that freeze this
          panel. */}
      <h3 style={{ marginBottom: 4 }}>Site access outside Live mode</h3>
        {/* Says whether this is happening NOW, rather than describing a
            hypothetical an operator has to work out for themselves. */}
        <div
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            marginBottom: 12,
            // MATCHES THE MODE, like everywhere else in the admin: demo is
            // amber, test is blue. This hardcoded amber for both, so the
            // box said "Test mode" in a demo-coloured frame.
            border:
              "1px solid " +
              (!active
                ? "var(--border-color)"
                : settings?.siteMode === "test"
                  ? "var(--accent-blue)"
                  : "var(--accent-yellow)"),
            fontSize: 13,
          }}
        >
          {active ? (
            <>
              {/* Names the mode. "Not in Live mode" is true and makes the
                  reader work out which of the two it is, when the answer
                  is right here. */}
              <strong>
                {settings?.siteMode === "demo" ? "Demo" : "Test"} mode — in effect now.
              </strong>{" "}
              The public cannot reach the site.
            </>
          ) : (
            <>
              <strong>Not in effect.</strong> The site is Live and open to everyone. These
              settings apply the moment root switches to Demo or Test.
            </>
          )}
        </div>

      <Frozen frozen={frozen} title={frozenTitle}>
        <p style={{ fontSize: 13, opacity: 0.8, marginTop: 0 }}>
          While the site is in Demo or Test mode it closes to the public — including people who
          are not signed in at all. Only those with an email address at your own domain can
          reach it; everyone else sees a maintenance notice, and the public API answers 503.
        </p>


        <label style={{ display: "block", fontSize: 13, marginBottom: 4 }}>
          Your domain
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder={rule.domain || "derived from your site address"}
            disabled={locked || busy}
            title={frozen ? frozenTitle : undefined}
            style={{ flex: "1 1 260px" }}
          />
          <button
            type="button"
            disabled={locked || busy}
            onClick={() => save({ siteEmailDomain: domain.trim() })}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
        <p style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
          Leave blank to use the address your site answers on.
          {settings?.rootEmail && (
            <>
              {" "}
              Root is <strong>{settings.rootEmail}</strong> and must always satisfy this — a
              domain it fails will be refused.
            </>
          )}
          {rule.domain && (
            <>
              {" "}
              Currently <strong>{rule.domain}</strong>, which accepts{" "}
              {(rule.accepts || []).map((a, i) => (
                <span key={a}>
                  {i > 0 ? ", " : ""}
                  <code>{a}</code>
                </span>
              ))}
              .
            </>
          )}
        </p>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: 13,
            marginTop: 14,
          }}
        >
          <input
            type="checkbox"
            checked={!!settings?.allowAdminsAnyEmailDomain}
            disabled={locked || busy}
            title={frozen ? frozenTitle : undefined}
            onChange={(e) => save({ allowAdminsAnyEmailDomain: e.target.checked })}
            style={{ marginTop: 3 }}
          />
          <span>
            <strong>Allow admin users to use any email address</strong>
            <br />
            <span style={{ opacity: 0.75 }}>
              For an operator whose admins are on assorted addresses. Ordinary users are still
              held to the domain, and <strong>root always is</strong> — the account that can
              change this must never be able to lock itself out.
            </span>
          </span>
        </label>
      </Frozen>
    </section>
  );
}
